import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { useDesignerStore } from '../store/designerStore.js';
import { registry } from '../core/Registry.js';

// Der äußere Wrapper, der dem Widget im Designer seinen Rahmen und seine CSS-Styles gibt
const WidgetWrapper = styled.div`
  position: relative;
  transition: all 0.2s ease;
  
  /* Basis Styling aus dem JSON anwenden */
  ${props => props.$inlineStyles}

  /* Hover & Selected States (nur im Designer, nicht im Live-Frontend) */
  ${props => !props.$isLiveMode && css`
    border: 1px solid transparent;
    
    &:hover {
      border-color: #00A9E0;
      > .widget-overlay {
        opacity: 1;
      }
    }
    
    ${props.$isSelected && css`
      border-color: #00A9E0;
      box-shadow: 0 0 0 2px rgba(0, 169, 224, 0.2);
    `}
  `}
`;

const WidgetOverlay = styled.div`
  position: absolute;
  top: -24px;
  right: -1px;
  background: #00A9E0;
  color: white;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px 4px 0 0;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
  display: flex;
  align-items: center;
  gap: 5px;
  z-index: 10;
`;

/**
 * Wandelt unser JSON-Style-Objekt in einen CSS-String um
 * @param {object} styleObj z.B. { desktop: { padding: '20px', display: 'flex' } }
 * @param {string} deviceMode 'desktop', 'tablet', 'mobile'
 */
function buildInlineStyles(styleObj, deviceMode) {
  if (!styleObj) return '';
  
  // Kaskadierendes CSS-Merging (Desktop First)
  // Desktop vererbt an Tablet, Tablet vererbt an Mobile
  let styles = { ...(styleObj.desktop || {}) };
  
  if (deviceMode === 'tablet' || deviceMode === 'mobile') {
    styles = { ...styles, ...(styleObj.tablet || {}) };
  }
  
  if (deviceMode === 'mobile') {
    styles = { ...styles, ...(styleObj.mobile || {}) };
  }
  let cssString = '';
  
  // Mapping camelCase -> kebab-case
  for (const [key, value] of Object.entries(styles)) {
    if (value === undefined || value === null || value === '') continue; // Überspringe leere Eingaben
    const kebabKey = key.replace(/([A-Z])/g, "-$1").toLowerCase();
    cssString += `${kebabKey}: ${value};\n`;
  }
  return cssString;
}

export function BaseWidget({ node, isLiveMode = false }) {
  const { selectedNodeId, setSelectedNodeId, deviceMode } = useDesignerStore();
  
  // Hole die Definition aus der Registry
  const widgetConfig = registry.getWidget(node.type);
  
  const isSelected = selectedNodeId === node.id;

  // CSS Styles aus dem JSON extrahieren und an das aktuelle Device anpassen
  const inlineStyles = useMemo(() => {
    return buildInlineStyles(node.style, deviceMode);
  }, [node.style, deviceMode]);

  const handleClick = (e) => {
    if (isLiveMode) return;
    e.stopPropagation(); // Verhindert, dass der Klick an den Parent-Container weitergegeben wird
    setSelectedNodeId(node.id);
  };

  const handleSaveAsTemplate = async (e) => {
    e.stopPropagation();
    const templateName = window.prompt(`Wie soll das Template für "${widgetConfig.label}" heißen?`);
    if (!templateName) return;

    try {
      const token = localStorage.getItem('tef-auth-token') || localStorage.getItem('adminToken');
      // Bereinige IDs, um Kollisionen bei Wiederverwendung zu vermeiden? Optional. Hier speichern wir das reine JSON.
      const payload = {
        name: templateName,
        type: widgetConfig.category === 'layout' ? 'section' : 'widget',
        content: [node] // Wir packen die Node als Array rein, wie bei einer Standard Page
      };

      const res = await fetch('/api/admin/designer/templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.ok) {
        alert('Erfolgreich als Template gespeichert!');
      } else {
        alert('Fehler beim Speichern: ' + data.error);
      }
    } catch (err) {
      console.error(err);
      alert('Kritischer Fehler beim Speichern.');
    }
  };

  if (!widgetConfig) {
    return <div style={{ color: 'red', border: '1px solid red', padding: '10px' }}>Widget Typ "{node.type}" nicht gefunden!</div>;
  }

  const Component = widgetConfig.component;

  return (
    <WidgetWrapper 
      $isSelected={isSelected} 
      $isLiveMode={isLiveMode}
      $inlineStyles={inlineStyles}
      onClick={handleClick}
      className={node.advanced?.cssClasses || ''}
      id={node.advanced?.cssId || node.id}
    >
      {!isLiveMode && (
        <WidgetOverlay className="widget-overlay">
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span>{widgetConfig.icon}</span>
            {widgetConfig.label}
          </span>
          {/* Action Icons im Overlay */}
          <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto', paddingLeft: '8px' }}>
            <button 
              title="Als Template speichern"
              onClick={handleSaveAsTemplate}
              style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: '2px', display: 'flex', opacity: 0.8 }}
              onMouseEnter={e => e.currentTarget.style.opacity = 1}
              onMouseLeave={e => e.currentTarget.style.opacity = 0.8}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
            </button>
          </div>
        </WidgetOverlay>
      )}
      
      {/* Das echte Widget rendern und ihm eventuelle Children übergeben (für Container) */}
      <Component node={node} isLiveMode={isLiveMode}>
        {node.children && node.children.map(child => (
          <BaseWidget key={child.id} node={child} isLiveMode={isLiveMode} />
        ))}
      </Component>
    </WidgetWrapper>
  );
}
